/* import __COLOCATED_TEMPLATE__ from './trending-topics-panel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import {
  type TrendingTopic,
  TrendingTopicTimeTrend,
} from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/trending-topics-data-resources';
import type Range from 'embercom/models/reporting/range';
import type { Topic } from 'embercom/components/reporting/automation/ai-insights/fin-insights/topics-data-resources';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type { TrendingTopicConversation } from 'embercom/components/reporting/automation/ai-insights/trending-topics-key-conversations';
import { convertMarkdownToHtml } from './optimize/summary-converter';
import moment from 'moment-timezone';
import RangeModel from 'embercom/models/reporting/range';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';
import { cached } from 'tracked-toolbox';
import type IntlService from 'ember-intl/services/intl';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { type Filter, type LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { type FlexibleQueryTimeRange } from 'embercom/services/reporting-csv-export';
import { buildFiltersForDataConfig } from 'embercom/lib/reporting/custom/data-config-builder-helpers';

const FEEDBACK_SURVEY_ID = 50178727;
const PANEL_KEY_CONVERSATIONS = 'key-conversations' as const;
const PANEL_SUBTOPIC_CONVERSATIONS = 'subtopic-conversations' as const;

type FeedbackValue = 'positive' | 'negative';
type ConversationsPanelShowing =
  | typeof PANEL_KEY_CONVERSATIONS
  | typeof PANEL_SUBTOPIC_CONVERSATIONS
  | null;

interface Signature {
  Args: {
    showSidePanel: boolean;
    onClose: () => void;
    trendingTopic: TrendingTopic | undefined;
    selectedTopic: Topic | null;
    selectedSubTopic: string | null;
    conversationCount: number;
    isStandalone: boolean;
    trackAnalyticsEvent: (
      action: string,
      object: string,
      extraContext: Record<string, any>,
    ) => void;
  };
}

export default class TrendingTopicsPanel extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingChartService: ReportingChartService;

  @tracked conversationsPanelShowing: ConversationsPanelShowing = null;
  @tracked loadedConversationsForPanel: TrendingTopicConversation[] = [];
  @tracked initialSelectedConversationId: number | undefined = undefined;
  @tracked hasProvidedFeedback = false;
  @tracked allSubtopicConversations: TrendingTopicConversation[] = [];

  get showingKeyConversations(): boolean {
    return this.conversationsPanelShowing === PANEL_KEY_CONVERSATIONS;
  }

  get showConversationsPanel(): boolean {
    return this.conversationsPanelShowing !== null;
  }

  get trendHeader() {
    if (!this.args.trendingTopic) {
      return this.intl.t(
        `components.reporting.automation.fin-insights.analyse.topic-trends.trending-topic-panel.header.unknown`,
      );
    }

    let anomaly = TrendingTopicTimeTrend.SingleWeekAnomaly;
    if (this.args.trendingTopic?.timeTrend === anomaly) {
      return this.intl.t(
        `components.reporting.automation.fin-insights.analyse.topic-trends.trending-topic-panel.header.anomaly`,
      );
    }

    return this.intl.t(
      `components.reporting.automation.fin-insights.analyse.topic-trends.trending-topic-panel.header.trend`,
    );
  }

  get chartHeader() {
    if (!this.args.trendingTopic) {
      return this.intl.t(`automation.ai-insights.trending-topics-panel.unknown`);
    }

    let anomaly = TrendingTopicTimeTrend.SingleWeekAnomaly;
    if (this.args.trendingTopic.timeTrend === anomaly) {
      return this.intl.t(`automation.ai-insights.trending-topics-panel.anomaly-over-time`);
    }

    return this.intl.t(`automation.ai-insights.trending-topics-panel.trend-over-time`);
  }

  get formattedInsightExplanation(): string {
    return convertMarkdownToHtml(this.args.trendingTopic?.insightExplanation || '', true, true);
  }

  @cached
  get dateRange(): Range {
    // Calculate 12 complete weeks (Monday to Sunday) excluding the current incomplete week
    // Use the app's timezone consistently for calculation and storage
    let timezone = this.appService.app.timezone;
    let now = moment.tz(timezone);

    let lastCompleteWeekStart = now.clone().startOf('isoWeek').subtract(1, 'week');
    let lastCompleteWeekEnd = lastCompleteWeekStart.clone().endOf('isoWeek');

    let startDate = lastCompleteWeekStart.clone().subtract(11, 'weeks');

    // Pass ISO strings with timezone offset to preserve timezone info through the serialization chain
    return RangeModel.createFromParams(
      startDate.startOf('day').format(),
      lastCompleteWeekEnd.endOf('day').format(),
      timezone,
    );
  }

  @action
  onShowTrendingTopicsConversationPanel(conversations: TrendingTopicConversation[]) {
    if (conversations.length === 0) {
      return;
    }
    this.loadedConversationsForPanel = conversations;
    this.initialSelectedConversationId = conversations[0].id;
    this.conversationsPanelShowing = PANEL_KEY_CONVERSATIONS;
  }

  @action
  onConversationSelected(conversationId: number, conversations: TrendingTopicConversation[]) {
    if (conversations.length === 0) {
      return;
    }
    this.loadedConversationsForPanel = conversations;
    this.initialSelectedConversationId = conversationId;
    this.conversationsPanelShowing = PANEL_KEY_CONVERSATIONS;
  }

  @action
  onCloseTrendingTopicsConversationPanel() {
    this.conversationsPanelShowing = null;
    this.initialSelectedConversationId =
      this.loadedConversationsForPanel.length > 0
        ? this.loadedConversationsForPanel[0].id
        : undefined;
  }

  get metric() {
    return this.reportingMetrics.getMetricById('v1.new_conversations');
  }

  get timeRange(): FlexibleQueryTimeRange | undefined {
    if (!this.dateRange) {
      return undefined;
    }
    return {
      start: this.dateRange.startMoment.valueOf(),
      end: this.dateRange.endMoment.valueOf(),
      interval: this.dateRange.interval,
      property: this.metric.timeProperty,
    };
  }

  get attributeIds() {
    let attributeIds = [
      'conversation.id',
      'conversation.first_user.name',
      'conversation.first_user.pseudonym',
      'conversation.first_user.email',
      'conversation_custom_fields#ai_insights_conversation_summary',
      'conversation_custom_fields#ai_insights_subtopic',
      'conversation_custom_fields#ai_insights_topic',
      'conversation.ai_generated_metrics.csat_explanation',
      'conversation.ai_generated_metrics.csat',
    ];

    if (this.appService.app.useNewCxSummary) {
      attributeIds.push('conversation_custom_fields#customer_experience_summary');
    }

    if (this.appService.app.canSeeCxScoreAttributes) {
      attributeIds.push('conversation_custom_fields#customer_experience_attributes');
    }

    return attributeIds;
  }

  get subtopicFilterState(): LogicalFilter {
    // Filter by subtopic name instead of conversation IDs
    let filter: Filter[] = [
      {
        type: 'category',
        data: {
          attribute: 'conversation_custom_fields#ai_insights_subtopic',
          property: 'conversation_custom_fields#ai_insights_subtopic',
          values: [this.args.trendingTopic?.subtopicName ?? ''],
        },
      },
    ];

    return buildFiltersForDataConfig(
      null,
      this.metric,
      null,
      { type: 'and', filters: filter },
      false,
    ) as LogicalFilter;
  }

  @restartableTask
  *loadSubtopicConversations(numberOfConversations: number): TaskGenerator<void> {
    if (!this.args.trendingTopic?.subtopicName || !this.dateRange) {
      return;
    }

    let timeRange = this.timeRange!;
    let filterState = this.subtopicFilterState;

    let result = yield this.reportingUnderlyingDataService.loadDrillInResult(
      this.appService.app.id,
      this.metric.datasetId,
      filterState,
      0,
      numberOfConversations,
      timeRange,
      this.attributeIds,
      'conversation.first_user_conversation_part_created_at',
      'desc',
      this.reportingChartService.syntheticDataUseCase,
      this.appService.app.timezone,
      false,
    );

    this.allSubtopicConversations = result.data.map((conversation: any) => ({
      id: Number(conversation['conversation.id']),
      userName: conversation['conversation.first_user.name'],
      userPseudonym: conversation['conversation.first_user.pseudonym'],
      userEmail: conversation['conversation.first_user.email'],
      topicSummary: conversation['conversation_custom_fields#ai_insights_conversation_summary'],
      subtopic: conversation['conversation_custom_fields#ai_insights_subtopic'],
      topic: conversation['conversation_custom_fields#ai_insights_topic'],
      cxExplanation: this.appService.app.useNewCxSummary
        ? conversation['conversation_custom_fields#customer_experience_summary'] ||
          conversation['conversation.ai_generated_metrics.csat_explanation']
        : conversation['conversation.ai_generated_metrics.csat_explanation'],
      cxScore: conversation['conversation.ai_generated_metrics.csat'],
      cxAttributes: conversation['conversation_custom_fields#customer_experience_attributes'],
    })) as TrendingTopicConversation[];
  }

  @action
  async onShowAllSubtopicConversations() {
    await taskFor(this.loadSubtopicConversations).perform(100);

    if (this.allSubtopicConversations.length > 0) {
      this.loadedConversationsForPanel = this.allSubtopicConversations;
      this.initialSelectedConversationId = this.allSubtopicConversations[0].id;
      this.conversationsPanelShowing = PANEL_SUBTOPIC_CONVERSATIONS;
    }
  }

  @action
  onFeedback(feedback: FeedbackValue) {
    if (!this.args.trendingTopic) {
      return;
    }
    this.hasProvidedFeedback = true;
    this.args.trackAnalyticsEvent('feedback_submitted', 'trending_topics_panel', {
      topic: this.args.trendingTopic.topicName,
      subtopic: this.args.trendingTopic.subtopicName,
      feedback,
    });
    this.intercomEventService.trackEvent({
      event_name: 'feedback_survey_started',
      metadata: {
        location: 'trending_topics_panel',
      },
    });
    window.Intercom('startSurvey', FEEDBACK_SURVEY_ID);
  }

  @action
  async onCopyLink() {
    if (!this.args.trendingTopic) {
      return;
    }

    let url = new URL(window.location.href);
    url.searchParams.set('trendingTopic', this.args.trendingTopic.subtopicName);
    url.searchParams.set('trendingTopicWeek', this.args.trendingTopic.weekStart);

    await this.copyToClipboard(url.toString());

    this.args.trackAnalyticsEvent('link_copied', 'trending_topics_panel', {
      topic: this.args.trendingTopic.topicName,
      subtopic: this.args.trendingTopic.subtopicName,
    });

    this.notificationsService.notifyConfirmation(
      this.intl.t('automation.ai-insights.trending-topics-panel.copy-link-success'),
    );
  }

  private async copyToClipboard(text: string): Promise<void> {
    // requires HTTPS --doesnt work in dev
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (ignored) {
        // just ignore
      }
    }

    // fallback for dev env
    let textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::TrendingTopicsPanel': typeof TrendingTopicsPanel;
  }
}
